import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppContextProvider } from './context/AppContext.jsx';
import { authContext } from './context/index.js';
import { useContext } from 'react';
import ProtectedRoute from './utils/ProtectedRoute.jsx';
import TestLogin from './components/TestLogin.jsx';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Auth Pages
import Login from './pages/Auth/Login.jsx';
import Register from './pages/Auth/Register.jsx';

// Layout Components
import DashboardLayout from './components/Layout/DashboardLayout.jsx';

// Teacher Pages
import TeacherDashboard from './pages/Teacher/Dashboard.jsx';
import TeacherCourses from './pages/Teacher/Courses.jsx';
import CreateCourse from './pages/Teacher/CreateCourse.jsx';
import CourseLessons from './pages/Teacher/CourseLessons.jsx';
import TeacherStudents from './pages/Teacher/Students.jsx';

// Student Pages
import StudentDashboard from './pages/Student/Dashboard.jsx';
import StudentCourses from './pages/Student/Courses.jsx';
import StudentEnrollments from './pages/Student/Enrollments.jsx';
import CourseDetail from './pages/Student/CourseDetail.jsx';

// Shared Pages
import Settings from './pages/Settings.jsx';
import Profile from './pages/Profile.jsx';

// Landing Page
const LandingPage = () => {
  const { userData } = useContext(authContext);
  
  if (userData) {
    return <Navigate to={`/${userData.role}/dashboard`} replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-accent-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <span className="text-xl font-bold text-secondary-900">SkillForge</span>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="/login"
                className="text-secondary-600 hover:text-secondary-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Sign In
              </a>
              <a
                href="/register"
                className="bg-primary-600 text-white hover:bg-primary-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-secondary-900 mb-6">
            Master New Skills with
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-600">
              {' '}SkillForge
            </span>
          </h1>
          <p className="text-xl text-secondary-600 mb-8 max-w-3xl mx-auto">
            Join thousands of learners and instructors in our comprehensive online learning platform. 
            Create, share, and master skills that matter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/register"
              className="bg-primary-600 text-white hover:bg-primary-700 px-8 py-3 rounded-lg text-lg font-medium transition-colors"
            >
              Start Learning Free
            </a>
            <a
              href="/register?role=teacher"
              className="border border-primary-600 text-primary-600 hover:bg-primary-50 px-8 py-3 rounded-lg text-lg font-medium transition-colors"
            >
              Teach on SkillForge
            </a>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Why Choose SkillForge?
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              We provide everything you need to succeed in your learning journey
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📚</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">Expert Instructors</h3>
              <p className="text-secondary-600">
                Learn from industry professionals and experienced educators
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">Focused Learning</h3>
              <p className="text-secondary-600">
                Structured courses designed to help you achieve your goals
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🚀</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">Career Growth</h3>
              <p className="text-secondary-600">
                Build skills that advance your career and open new opportunities
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Learning?
          </h2>
          <p className="text-primary-100 mb-8 text-lg">
            Join SkillForge today and unlock your potential
        </p>
        <a
            href="/register"
            className="bg-white text-primary-600 hover:bg-gray-50 px-8 py-3 rounded-lg text-lg font-medium transition-colors inline-block"
          >
            Get Started Now
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-secondary-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-accent-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <span className="text-xl font-bold">SkillForge</span>
            </div>
            <p className="text-secondary-400 mb-4">
              Empowering learners and instructors worldwide
            </p>
            <p className="text-secondary-500 text-sm">
              © 2024 SkillForge. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Unauthorized Page
const UnauthorizedPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-secondary-50">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-secondary-900 mb-4">403</h1>
      <h2 className="text-2xl font-semibold text-secondary-700 mb-2">Access Denied</h2>
      <p className="text-secondary-600 mb-8">You don't have permission to access this page.</p>
      <a
        href="/"
        className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-3 rounded-lg font-medium transition-colors"
      >
        Go Home
      </a>
    </div>
  </div>
);

function App() {
  return (
    <AppContextProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/test-login" element={<TestLogin />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            
            {/* Teacher Routes */}
            <Route path="/teacher" element={
              <ProtectedRoute requiredRole="teacher">
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route path="dashboard" element={<TeacherDashboard />} />
              <Route path="courses" element={<TeacherCourses />} />
              <Route path="courses/create" element={<CreateCourse />} />
              <Route path="courses/:id/edit" element={<CreateCourse />} />
              <Route path="courses/:id/lessons" element={<CourseLessons />} />
              <Route path="students" element={<TeacherStudents />} />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            
            {/* Student Routes */}
            <Route path="/student" element={
              <ProtectedRoute requiredRole="student">
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route path="dashboard" element={<StudentDashboard />} />
              <Route path="courses" element={<StudentCourses />} />
              <Route path="courses/:id" element={<CourseDetail />} />
              <Route path="enrollments" element={<StudentEnrollments />} />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Toast Notifications */}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </div>
      </Router>
    </AppContextProvider>
  );
}

export default App;
